import { useState, useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import {
  Box,
  Button,
  Card,
  CardContent,
  styled,
  Typography,
} from '@mui/material';

import {
  SyncedTripLeg,
  TripLeg,
  TripLegStatusLogEntry,
} from '../../../data/models/reservation.model';
import {
  TripLegStatus,
  TripLegStatusBC,
} from '../../../data/models/trip-leg-status.model';
import { Passenger } from '../../../data/models/passenger.model';
import { TransportationAttribute } from '../../../data/models/transportation-attribute.model';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

import { useEta } from '../../../hooks/display/useEta';
import { usePageVisible } from '../../../hooks/display/usePageVisible';
import { usePortalName } from '../../../hooks/usePortalName';
import * as accountingSlice from '../../../data/accounting.slice';
import * as clientSlice from '../../../data/client.slice';
import * as driverSlice from '../../../data/driver.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as reservationSlice from '../../../data/reservation.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import reservationUtils from '../../../services/reservationUtils';
import CancelTripLegDialog from '../passengers/CancelTripLegDialog';
import dateUtils from '../../../services/DateUtils';
import { phoneUtils } from '../../../services/phoneUtils';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useIsClientPortalUser } from '../../../hooks/useIsClientPortalUser';
import CancelTripLegUnavailableDialog from '../passengers/CancelTripLegUnavailableDialog';

const POLLING_INTERVAL = 5000;

const PREFIX = 'TripLegDetails';

const classes = {
  container: `${PREFIX}-container`,
  detailWithIcons: `${PREFIX}-detail-with-icons`,
  detailWithMultiline: `${PREFIX}-detail-with-multiline`,
  fillSpacer: `${PREFIX}-fill-spacer`,
  actions: `${PREFIX}-actions`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'grid',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(2),
    gridTemplateColumns: '120px 199px',
    ['& .MuiTypography-subtitle2']: { margin: 'auto 0' },
    ['& .center-vertically']: { margin: 'auto 0' },
  },
  [`& .${classes.detailWithIcons}`]: {
    display: 'flex',
    gap: theme.spacing(0),
  },
  [`& .${classes.detailWithMultiline}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  [`& .${classes.fillSpacer}`]: { flex: 1 },
  [`&.${classes.actions}`]: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(3),
  },
}));

const TripLegDetails = ({
  tripLeg,
  tripLegPurpose,
  syncedTripLeg,
  isOnlyTripLeg,
  passenger,
  statusLogs = [],
}: {
  tripLeg: TripLeg;
  tripLegPurpose: string;
  syncedTripLeg: SyncedTripLeg | null;
  isOnlyTripLeg: boolean;
  passenger: Passenger;
  statusLogs?: TripLegStatusLogEntry[];
}) => {
  const t = useCommonTranslation();
  const navigate = useNavigate();
  const isSuperReaderUser = useIsSuperReaderUser();

  const isPageVisible = usePageVisible();
  const isPortal = usePortalName() ? true : false;
  const isClientPortalUser = useIsClientPortalUser();
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPolling, setIsPolling] = useState(true); // TODO: implement pause/resume polling
  const [cancelTripConfirmDialogOpen, setCancelTripConfirmDialogOpen] =
    useState(false);
  const [cancelTripUnavailable, setCancelTripUnavailable] = useState(false);
  const dispatch = useAppDispatch();
  const tripLegDetails = useAppSelector(tripLegSlice.selectTripLegDetails);
  const selectedTripLegDetails = tripLegDetails[tripLeg.tripLegReferenceID!];

  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  const provider = useAppSelector((state) =>
    tripLeg?.transportationProviderID
      ? providerSlice.selectProviderByID(
          state,
          tripLeg.transportationProviderID,
        )
      : undefined,
  );

  const requestedProvider = useAppSelector((state) =>
    tripLeg?.requestedTransportationProviderID
      ? providerSlice.selectProviderByID(
          state,
          tripLeg.requestedTransportationProviderID,
        )
      : undefined,
  );

  const canShowStatusOnClientPortal = (status: TripLegStatus) => {
    const allowedStatuses = [
      TripLegStatus.InTransit,
      TripLegStatus.Onsite,
      TripLegStatus.Onboard,
    ];
    return allowedStatuses.includes(status);
  };

  // Get the text for the status on the client portal
  const getClientPortalStatusText = (status: TripLegStatus) => {
    switch (status) {
      case TripLegStatus.InTransit:
        return t('tripStatusTrack.statusInTransit');
      case TripLegStatus.Onsite:
        return t('tripStatusTrack.statusOnsite');
      case TripLegStatus.Onboard:
        return t('tripStatusTrack.statusOnboard');
      default:
        return '';
    }
  };

  const isTripLegInProgress = reservationUtils.isTripLegInProgress(tripLeg);
  const isTripLegCancelable = reservationUtils.isTripLegCancelable(tripLeg);
  const isNoLoad = tripLeg?.status === TripLegStatus.NoLoad;

  const noLoadDateTime = useMemo(() => {
    if (!isNoLoad || !statusLogs.length) return null;
    const tz = tripLeg?.pickUpAddress?.timeZone;
    if (!tz) return null;
    const log = [...statusLogs]
      .reverse()
      .find((l) => l.tripLegStatusBC === TripLegStatusBC.NoLoad);
    return log ? DateTime.fromISO(log.createdOnUTC).setZone(tz) : null;
  }, [isNoLoad, statusLogs, tripLeg]);

  const driverLocationsById = useAppSelector(
    driverSlice.selectDriverLocationsById,
  );
  const driverId = syncedTripLeg?.driverID;
  const driverLocation = driverId ? driverLocationsById[driverId] : null;
  const driverEta = useEta(driverLocation?.estimatedTimeOfArrivalSeconds);

  const isCancellable = reservationUtils.canPortalUserEditTripLeg(
    syncedTripLeg,
    isPortal,
  );

  useEffect(() => {
    if (
      tripLeg &&
      tripLeg.tripLegReferenceID &&
      !isPortal &&
      !isClientPortalUser
    ) {
      dispatch(
        providerSlice.fetchInternalNoteAsync(tripLeg.tripLegReferenceID),
      );
    }
  }, [tripLeg]);

  const handleCancelTrip = () => {
    if (isCancellable) {
      setCancelTripConfirmDialogOpen(true);
    } else {
      // Show Cancel Trip Dialog
      setCancelTripUnavailable(true);
    }
  };

  const handleCancelTripDialogClose = (data: any) => {
    const tripLegStatusReasonBCs: string[] = [];
    const isRideshare = !!(
      tripLeg.transportationProviderID &&
      tripLeg.transportationProviderID ===
        provider?.rideshare?.transportationProviderID
    );
    if (data) {
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          tripLegStatusReasonBCs.push(data[key]);
        }
      });

      if (isRideshare && syncedTripLeg) {
        dispatch(
          reservationSlice.cancelTripLegAsync(
            passenger.passengerUUID!,
            syncedTripLeg.reservationReferenceID ??
              syncedTripLeg.reservation?.reservationReferenceID,
            syncedTripLeg.tripLegReferenceID!,
            tripLegStatusReasonBCs,
          ),
        );
      } else {
        if (isOnlyTripLeg) {
          dispatch(
            reservationSlice.cancelReservationAsync(
              passenger.passengerUUID!,
              tripLeg.reservationReferenceID!,
              tripLegStatusReasonBCs,
            ),
          );
        } else {
          dispatch(
            reservationSlice.cancelTripLegAsync(
              passenger.passengerUUID!,
              tripLeg.reservationReferenceID!,
              tripLeg.tripLegReferenceID!,
              tripLegStatusReasonBCs,
            ),
          );
        }
      }
      navigate(`/passengers/${passenger.passengerUUID}/`);
    }

    setCancelTripConfirmDialogOpen(false);
  };

  const [passengerPhone, setPassengerPhone] = useState('');

  useEffect(() => {
    if (passenger?.passengerPhones?.length) {
      const ph = passenger.passengerPhones[0];
      if (ph) {
        setPassengerPhone(phoneUtils.formatPhone(ph));
      }
    }
  }, [passenger]);

  useEffect(() => {
    if (tripLeg.transportationProviderID && !provider) {
      dispatch(
        providerSlice.fetchProviderAsync(tripLeg.transportationProviderID),
      );
    }
  }, [tripLeg.transportationProviderID]);

  useEffect(() => {
    if (tripLeg.requestedTransportationProviderID && !requestedProvider) {
      dispatch(
        providerSlice.fetchRequestedProviderAsync(
          tripLeg.requestedTransportationProviderID,
        ),
      );
    }
  }, [tripLeg.requestedTransportationProviderID]);

  const getAttributeByUUID = (uuid: string) => {
    return transportationAttributes.find(
      (a) => a.transportationAttributeUUID === uuid,
    );
  };

  const getAttributes = (
    tripLeg: TripLeg,
    attrBC: string,
  ): TransportationAttribute[] => {
    const attrs: TransportationAttribute[] = [];
    tripLeg.transportationAttributes.forEach((ta) => {
      let attr = getAttributeByUUID(ta[0]);
      if (attr?.transportationAttributeCategoryBC === attrBC) {
        attrs.push(attr);
        for (let i = 1; i < ta.length; i++) {
          attr = getAttributeByUUID(ta[i]);
          if (attr) {
            attrs.push(attr);
          }
        }
      }
    });
    return attrs;
  };

  const getTripMode = useMemo(() => {
    const attrs = getAttributes(tripLeg, 'AMBULATORY');
    return attrs.map((a) => a.name).join(', ');
  }, [tripLeg]);

  const getServiceLevel = useMemo(() => {
    const attrs = getAttributes(tripLeg, 'TRANSPORTMOBILITY');
    return attrs.map((a) => a.name).join(', ');
  }, [tripLeg]);

  // not using reservationUtils.getVehicleName because we need vehicleModel instead of vehicleLicensePlate
  const getVehicleName = useMemo(() => {
    const vehicle = selectedTripLegDetails?.vehicle;
    if (!vehicle) return '-';

    return `${vehicle.vehicleColor} ${vehicle.vehicleMake} ${vehicle.vehicleModel}`;
  }, [selectedTripLegDetails]);

  useEffect(() => {
    const pollingCallback = () => {
      if (syncedTripLeg?.driverID && isTripLegInProgress) {
        dispatch(driverSlice.fetchDriverLocationAsync(syncedTripLeg.driverID));
      }
    };

    const stopPolling = () => {
      clearInterval(timerIntervalRef.current as NodeJS.Timeout);
    };

    const startPolling = () => {
      pollingCallback();
      timerIntervalRef.current = setInterval(pollingCallback, POLLING_INTERVAL);
    };

    if (
      isPageVisible &&
      isPolling &&
      isTripLegInProgress &&
      syncedTripLeg?.driverID
    ) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [
    isPageVisible,
    isPolling,
    isTripLegInProgress,
    syncedTripLeg?.driverID,
    dispatch,
  ]);

  useEffect(() => {
    if (tripLeg.transportationProviderID) {
      dispatch(
        providerSlice.fetchProviderAsync(tripLeg.transportationProviderID),
      );
    }
  }, [tripLeg.transportationProviderID]);

  return (
    <>
      <Card className="bg-primary-shade">
        <CardContent>
          <StyledBox className={classes.container}>
            {/* ETA */}
            <Box display="flex" flexDirection="column">
              <Typography variant="caption">
                {isTripLegInProgress
                  ? t('tripStatusTrack.eta')
                  : isNoLoad
                    ? t('tripStatusTrack.noLoadTime')
                    : t('tripStatusTrack.appointmentTime')}
              </Typography>
              {isTripLegInProgress && driverEta && (
                <>
                  <Typography variant="caption" fontWeight="700">
                    {driverEta}
                  </Typography>
                </>
              )}
              {!isTripLegInProgress && isNoLoad && (
                <>
                  <Typography fontWeight="600">
                    {noLoadDateTime
                      ? dateUtils.formatDate(noLoadDateTime)
                      : tripLeg.appointmentDate}
                  </Typography>
                  <Typography color="text.secondary">
                    {noLoadDateTime && dateUtils.formatTime(noLoadDateTime)}
                  </Typography>
                </>
              )}
              {!isTripLegInProgress && !isNoLoad && (
                <>
                  <Typography fontWeight="600">
                    {tripLeg.appointmentDate}
                  </Typography>
                  <Typography color="text.secondary">
                    {tripLeg.appointmentDateTime &&
                      dateUtils.formatAppointmentTime(
                        tripLeg.appointmentDateTime,
                        tripLeg.dropOffAddress.timeZone!,
                      )}
                    {tripLeg.isWillCall ? t('reservation.willCallTime') : ''}
                  </Typography>
                </>
              )}
            </Box>
            {/* Status */}
            <Box display="flex" flexDirection="column">
              {!isPortal && syncedTripLeg && (
                <>
                  <Typography variant="caption">
                    {t('tripStatusTrack.status')}
                  </Typography>
                  <Typography fontWeight="600">{tripLeg.status}</Typography>
                </>
              )}
              {isPortal &&
                canShowStatusOnClientPortal(
                  tripLeg.status as TripLegStatus,
                ) && (
                  <>
                    <Typography variant="caption">
                      {t('tripStatusTrack.status')}
                    </Typography>
                    <Typography fontWeight="600">
                      {getClientPortalStatusText(
                        tripLeg.status as TripLegStatus,
                      )}
                    </Typography>
                  </>
                )}
            </Box>
            {/* Passenger */}
            <Typography variant="body2" className="center-vertically">
              {t('tripStatusTrack.passenger')}
            </Typography>
            <Box className={classes.detailWithIcons}>
              <Typography variant="subtitle2">
                {passenger.firstName} {passenger.lastName}
              </Typography>
              {/* TODO: Uncomment when call/message integration is available, as per product's decision this feature is not available in V1.3 */}
              {/* <Box className={classes.fillSpacer} />
            {syncedTripLeg && (
              <IconButton sx={{ p: 0.75 }}>
                <ChatBubbleOutlineRounded fontSize="small" />
              </IconButton>
            )}
            {syncedTripLeg && (
              <IconButton sx={{ p: 0.75 }}>
                <CallOutlined fontSize="small" />
              </IconButton>
            )} */}
            </Box>
            {/* Passenger Phone */}
            <Typography variant="body2">
              {t('tripStatusTrack.passengerPhone')}
            </Typography>
            <Typography variant="subtitle2">{passengerPhone}</Typography>
            {/* Provider */}
            <>
              <Typography variant="body2">
                {t('tripStatusTrack.provider')}
              </Typography>
              <Typography variant="subtitle2">
                {provider?.transportationProviderName || '-'}
              </Typography>
            </>
            {requestedProvider && (
              <>
                <Typography variant="body2">
                  {t('reservation.requestedProvider')}
                </Typography>
                <Typography variant="subtitle2">
                  {requestedProvider?.transportationProviderName}
                </Typography>
              </>
            )}
            {/* Driver */}
            {syncedTripLeg && (
              <>
                <Typography variant="body2">
                  {t('tripStatusTrack.driver')}
                </Typography>
                <Typography variant="subtitle2">
                  {reservationUtils.getDriverName(syncedTripLeg?.driver) ?? '-'}
                </Typography>
              </>
            )}
            {/* Vehicle */}
            <Typography variant="body2">
              {t('tripStatusTrack.vehicle')}
            </Typography>
            <Box className={classes.detailWithMultiline}>
              <Typography variant="subtitle2">{getVehicleName}</Typography>
              {selectedTripLegDetails?.vehicle?.licensePlate && (
                <Typography variant="subtitle2">
                  ({selectedTripLegDetails?.vehicle?.licensePlate})
                </Typography>
              )}
            </Box>
            {/* TripLeg ID */}
            {syncedTripLeg && (
              <>
                <Typography variant="body2">
                  {t('tripStatusTrack.tripLegId')}
                </Typography>
                <Typography variant="subtitle2">
                  {tripLeg.tripLegReferenceID}
                </Typography>
              </>
            )}
            {/* Trip Purpose */}
            <Typography variant="body2">
              {t('tripStatusTrack.tripPurpose')}
            </Typography>
            <Typography variant="subtitle2">{tripLegPurpose}</Typography>
            {/* Trip Mode */}
            <Typography variant="body2">
              {t('tripStatusTrack.tripMode')}
            </Typography>
            <Typography variant="subtitle2">{getTripMode}</Typography>
            {/* Service Level */}
            <Typography variant="body2">
              {t('tripStatusTrack.serviceLevel')}
            </Typography>
            <Typography variant="subtitle2">{getServiceLevel}</Typography>
            {/* Passenger Count */}
            <Typography variant="body2">
              {t('reservation.passengerCount')}
            </Typography>
            <Typography variant="subtitle2">
              {tripLeg.companionCount + 1}
            </Typography>
            {/* Pickup */}
            <Typography variant="body2">
              {t('tripStatusTrack.pickup')}
            </Typography>
            <Typography variant="subtitle2">
              {tripLeg.isWillCall
                ? t('reservation.willCallTime')
                : dateUtils.formatPickupDateTime(
                    tripLeg.pickUpDueDateTime,
                    tripLeg.pickUpAddress.timeZone!,
                  )}
            </Typography>
            {/* Pickup Address */}
            <Typography variant="body2">
              {t('tripStatusTrack.pickupAddress')}
            </Typography>
            <Box className={classes.detailWithMultiline}>
              <Typography variant="subtitle2">
                {tripLeg.pickUpAddress?.addressLine1}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${tripLeg.pickUpAddress?.city}, ${tripLeg.pickUpAddress?.stateCode} ${tripLeg.pickUpAddress?.zipCode}`}
              </Typography>
            </Box>
            {/* Pickup Phone */}
            <Typography variant="body2">
              {t('tripStatusTrack.pickupPhone')}
            </Typography>
            <Typography variant="subtitle2">
              {phoneUtils.formatPhone(tripLeg.pickUpPhone)}
            </Typography>
            {/* Pickup Notes */}
            <Typography variant="body2">
              {t('tripStatusTrack.pickupNote')}
            </Typography>
            <Typography variant="subtitle2">
              {/* @ts-ignore */}
              {tripLeg?.pickUpNote}
            </Typography>
            {/* Dropoff address */}
            <Typography variant="body2">
              {t('tripStatusTrack.dropoffAddress')}
            </Typography>
            <Box className={classes.detailWithMultiline}>
              <Typography variant="subtitle2">
                {tripLeg.dropOffAddress?.addressLine1}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`${tripLeg.dropOffAddress?.city}, ${tripLeg.dropOffAddress?.stateCode} ${tripLeg.dropOffAddress?.zipCode}`}
              </Typography>
            </Box>
            {/* Dropoff Phone */}
            <Typography variant="body2">
              {t('tripStatusTrack.dropoffPhone')}
            </Typography>
            <Typography variant="subtitle2">
              {phoneUtils.formatPhone(tripLeg.dropOffPhone)}
            </Typography>
            {/* Drop-off Notes */}
            <Typography variant="body2">
              {t('tripStatusTrack.dropoffNote')}
            </Typography>
            <Typography variant="subtitle2">
              {/* @ts-ignore */}
              {tripLeg?.dropOffNote}
            </Typography>
          </StyledBox>

          {/* Cancel trip button */}
          {syncedTripLeg && !isSuperReaderUser && (
            <>
              <StyledBox className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCancelTrip}
                  disabled={!isTripLegCancelable}
                  data-fs-element="CancelTripButton">
                  {t('tripStatusTrack.cancelTrip')}
                </Button>
              </StyledBox>

              <CancelTripLegDialog
                open={cancelTripConfirmDialogOpen}
                close={handleCancelTripDialogClose}
                dialogTitle={t('passenger.cancelTripLeg')}
                dialogMessage={t('passenger.cancelTripLegMessage')}
                submitDataFsElement="CancelTripSubmitButton"
              />
            </>
          )}
        </CardContent>
      </Card>
      <CancelTripLegUnavailableDialog
        dialogOpen={cancelTripUnavailable}
        loadingSyncedTripLeg={false}
        showUnavailableMessage={cancelTripUnavailable}
        onClose={() => setCancelTripUnavailable(false)}
      />
    </>
  );
};

export default TripLegDetails;
